// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import {
  cachedGet,
  handleApiCall,
  invalidateGetCache,
} from "../utils/apiHelper";

const CONFIG_CACHE_TTL = 30_000;
const configUrl = (tenantId) => `/admin/settings/tenant/${tenantId}/config`;

const invalidateTenantConfig = (tenantId) => {
  invalidateGetCache(configUrl(tenantId));
};

export const tenantSettingsService = {
  createSetting: (settingData) =>
    handleApiCall(
      () => apiClient.post("/admin/settings/tenant", settingData),
      "Error creating tenant setting",
    ),

  getTenantConfig: async (tenantId) => {
    const response = await cachedGet(configUrl(tenantId), {
      ttl: CONFIG_CACHE_TTL,
    });
    return response.data?.settings || {};
  },

  getTenantSettings: async (tenantId) => {
    if (!tenantId) return null;
    const settings = await tenantSettingsService.getTenantConfig(tenantId);
    return settings && typeof settings === "object" ? settings : {};
  },

  parseSettingsByCategory: (settings = []) => {
    if (!Array.isArray(settings)) return {};
    return settings.reduce((acc, setting) => {
      const category = setting.category || "general";
      const key = setting.key;
      if (!key) return acc;
      if (!acc[category]) acc[category] = {};
      acc[category][key] = setting.value;
      return acc;
    }, {});
  },

  updateTenantConfig: async (tenantId, settings) => {
    const result = await handleApiCall(
      () => apiClient.put(configUrl(tenantId), { settings }),
      "Error updating tenant config",
    );
    invalidateTenantConfig(tenantId);
    return result;
  },

  // Tenant logo is stored via the shared picture API, keyed by tenant_id
  // (the same endpoint used for user pictures). Bytes live in Valkey; nothing
  // logo-related is persisted in the tenant config anymore.
  uploadTenantLogo: async (tenantId, file) => {
    const formData = new FormData();
    formData.append("file", file);

    return handleApiCall(
      () =>
        apiClient.post(`/users/${tenantId}/picture`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      "Error uploading tenant logo",
    );
  },

  // Fetches the tenant logo through the authenticated axios client and returns
  // a blob object URL usable as an <img> src. Returns null when no logo exists
  // (404) so callers can fall back to a default. Caller owns the returned URL
  // and must URL.revokeObjectURL() it when done.
  getTenantLogoUrl: async (tenantId) => {
    if (!tenantId) return null;
    try {
      const response = await apiClient.get(`/users/${tenantId}/picture`, {
        responseType: "blob",
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error("Failed to load tenant logo:", err);
      }
      return null;
    }
  },
};
